import { useAuth } from "AuthProvider";
import useFetch from "hooks/useFetch";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  Select,
  Skeleton,
  Table,
  TextField,
  useConfirm,
  useToast,
} from "stories/Admin";
import styles from "./AdminsPage.module.scss";

/*
 * Admins for moskeen. En admin gir en annen person tilgang ved å skrive hele e-postadressen
 * personen bruker i iBønn-appen. Brukerlisten vises aldri; søket sier bare om e-posten er registrert.
 */

interface MosqueAdmin {
  id: number;
  email: string;
  username: string;
  isYou: boolean;
  grantedByEmail: string;
  createdAt: string;
}

interface ManagedMosque {
  orgNr: string;
  name: string;
}

interface AdminLookup {
  registered: boolean;
  alreadyAdmin: boolean;
}

const formatDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("nb-NO", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

const looksLikeEmail = (value: string) =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim());

const AdminsPage: React.FC = () => {
  const { selectedOrgNumber, selectedOrgName, mosqueName } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const listApi = useFetch<MosqueAdmin[]>();
  const lookupApi = useFetch<AdminLookup>();
  const grantApi = useFetch<MosqueAdmin>();
  const removeApi = useFetch<unknown>();
  const mosquesApi = useFetch<ManagedMosque[]>();
  const listRef = useRef(listApi.execute);
  listRef.current = listApi.execute;
  const mosquesRef = useRef(mosquesApi.execute);
  mosquesRef.current = mosquesApi.execute;

  // Admin for hovedmoskeen kan også gi admin til undermoskeene (orgnr_1, orgnr_2 …).
  const [mosques, setMosques] = useState<ManagedMosque[]>([]);
  const [target, setTarget] = useState<string | null>(selectedOrgNumber);

  const [admins, setAdmins] = useState<MosqueAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [searchedEmail, setSearchedEmail] = useState("");
  const [lookup, setLookup] = useState<AdminLookup | null>(null);
  const [searching, setSearching] = useState(false);
  const [granting, setGranting] = useState(false);

  const ownName = mosqueName || selectedOrgName || "moskeen";
  const shownMosque =
    target && target !== selectedOrgNumber
      ? (mosques.find((m) => m.orgNr === target)?.name ?? target)
      : ownName;

  useEffect(() => {
    setTarget(selectedOrgNumber);
    setMosques([]);
    if (!selectedOrgNumber) return;
    mosquesRef
      .current(`/api/v1/mosques/${selectedOrgNumber}/admins/mosques`, "GET")
      .then((result) => setMosques(Array.isArray(result) ? result : []));
  }, [selectedOrgNumber]);

  const load = useCallback(async () => {
    if (!target) return;
    setIsLoading(true);
    const result = await listRef.current(
      `/api/v1/mosques/${target}/admins`,
      "GET",
    );
    setAdmins(Array.isArray(result) ? result : []);
    setIsLoading(false);
  }, [target]);

  useEffect(() => {
    load();
    setLookup(null);
    setSearchedEmail("");
  }, [load]);

  const search = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const value = email.trim().toLowerCase();
    if (!looksLikeEmail(value)) return;
    setSearching(true);
    setLookup(null);
    setSearchedEmail(value);
    const result = await lookupApi.execute(
      `/api/v1/mosques/${target}/admins/lookup`,
      "POST",
      { email: value },
    );
    setLookup(result);
    setSearching(false);
  };

  const grant = async () => {
    const ok = await confirm({
      title: `Gi ${searchedEmail} admin?`,
      message: (
        <>
          Personen får samme tilgang som deg til <b>{shownMosque}</b> i
          admin-panelet og i appen: bønnetider, push-meldinger, plakater, TV og
          alt annet innhold.
        </>
      ),
      confirmLabel: "Gi admin",
    });
    if (!ok) return;
    setGranting(true);
    const result = await grantApi.execute(
      `/api/v1/mosques/${target}/admins`,
      "POST",
      { email: searchedEmail },
    );
    setGranting(false);
    if (result) {
      toast.success(`${searchedEmail} er nå admin for ${shownMosque}.`);
      setEmail("");
      setSearchedEmail("");
      setLookup(null);
      load();
    }
  };

  const remove = async (admin: MosqueAdmin) => {
    const ok = await confirm({
      title: admin.isYou
        ? "Fjerne din egen tilgang?"
        : `Fjerne ${admin.email} som admin?`,
      message: admin.isYou ? (
        <>
          Du mister tilgangen til <b>{shownMosque}</b> med en gang. En annen
          admin må gi deg tilgang igjen.
        </>
      ) : (
        <>
          Personen mister tilgangen til <b>{shownMosque}</b> med en gang.
        </>
      ),
      confirmLabel: "Fjern tilgang",
      tone: "danger",
    });
    if (!ok) return;
    await removeApi.execute(
      `/api/v1/mosques/${target}/admins/${admin.id}`,
      "DELETE",
    );
    load();
  };

  const lookupError = lookupApi.error?.code ?? grantApi.error?.code;
  const onlyOne = admins.length <= 1;

  const columns: Array<Column<MosqueAdmin>> = [
    {
      key: "email",
      header: "Admin",
      render: (a) => (
        <span className={styles.stack}>
          <span className={styles.inline}>
            <b>{a.email || "Ukjent bruker"}</b>
            {a.isYou && <Badge tone="mint">Deg</Badge>}
          </span>
          {a.username && <span className={styles.muted}>{a.username}</span>}
        </span>
      ),
    },
    {
      key: "created",
      header: "Lagt til",
      render: (a) => (
        <span className={styles.stack}>
          <span>{formatDate(a.createdAt)}</span>
          {a.grantedByEmail && (
            <span className={styles.muted}>av {a.grantedByEmail}</span>
          )}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (a) => (
        <Button
          size="sm"
          variant="danger"
          onClick={() => remove(a)}
          disabled={onlyOne}
          title={onlyOne ? "Moskeen må ha minst én admin" : undefined}
        >
          Fjern
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Administratorer"
        subtitle={`Hvem som kan administrere ${shownMosque} i admin-panelet og i appen.`}
      />

      {mosques.length > 1 && (
        <Card title="Moské">
          <span className={styles.muted}>
            Som admin for {ownName} kan du også gi admin-tilgang til
            undermoskeene.
          </span>
          <Select
            label="Gjelder"
            value={target ?? ""}
            onChange={(e) => setTarget(e.target.value)}
            options={mosques.map((m) => ({
              value: m.orgNr,
              label: `${m.name} (${m.orgNr})`,
            }))}
            fieldClassName={styles.email}
          />
        </Card>
      )}

      <Card title="Gi en person admin-tilgang">
        <span className={styles.muted}>
          Skriv hele e-postadressen personen har brukt til å lage en konto i
          iBønn-appen. Av personvernhensyn kan du ikke se listen over brukere,
          bare om e-posten er registrert.
        </span>
        <form className={styles.searchRow} onSubmit={search}>
          <TextField
            label="E-post"
            type="email"
            placeholder="navn@eksempel.no"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={254}
            autoComplete="off"
            fieldClassName={styles.email}
          />
          <Button
            type="submit"
            variant="secondary"
            loading={searching}
            disabled={!target || !looksLikeEmail(email)}
            icon={<Icon name="search" size={16} strokeWidth={2} />}
          >
            Søk
          </Button>
        </form>

        {(lookup || lookupError) && (
          <div className={styles.result}>
            {lookupError === 429 ? (
              <Alert tone="warn">
                Du har søkt mange ganger på kort tid. Vent en time og prøv
                igjen.
              </Alert>
            ) : lookupError === 404 ? (
              <Alert tone="warn">
                Fant ingen konto med {searchedEmail}. Be personen lage en konto
                i iBønn-appen først.
              </Alert>
            ) : lookupError ? (
              <Alert tone="error">Noe gikk galt. Prøv igjen.</Alert>
            ) : !lookup?.registered ? (
              <Alert tone="warn">
                Fant ingen konto med <b>{searchedEmail}</b>. Be personen laste
                ned iBønn-appen og lage en konto med denne e-posten under
                Profil, og søk igjen.
              </Alert>
            ) : lookup.alreadyAdmin ? (
              <Alert tone="info">
                <b>{searchedEmail}</b> er allerede admin for {shownMosque}.
              </Alert>
            ) : (
              <>
                <Alert tone="ok">
                  <b>{searchedEmail}</b> har en iBønn-konto.
                </Alert>
                <span>
                  <Button
                    onClick={grant}
                    loading={granting}
                    icon={<Icon name="plus" size={16} strokeWidth={2} />}
                  >
                    Gi admin for {shownMosque}
                  </Button>
                </span>
              </>
            )}
          </div>
        )}
      </Card>

      <Card title="Admins">
        {isLoading && admins.length === 0 ? (
          <div className={styles.skeletons}>
            {[0, 1].map((i) => (
              <Skeleton key={i} height={36} />
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            rows={admins}
            rowKey={(a) => a.id}
            caption="Admins for moskeen"
            empty={
              <EmptyState icon="users" title="Ingen admins">
                Gi en person admin-tilgang over.
              </EmptyState>
            }
          />
        )}
      </Card>
    </>
  );
};

export default AdminsPage;
